import { Divider } from '@eventbrite/eds-divider';
import { gettext } from '@eventbrite/i18n';
import {
    MagnifyingGlassIcon,
    MapPinIcon,
    Typography,
} from '@eventbrite/marmalade';
import {
    LocationProvider,
    useLocationContext,
} from '@eventbrite/search-takeover';
import loadable from '@loadable/component';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import React, { useState } from 'react';
import { DEFAULT_QUERYCLIENT_OPTIONS } from '../../../../constants/reactQuery';
import { useGlobalNavContext } from '../../../../state';
import { onEnterKey } from '../../../../utils';
import {
    getHeapDataAttribute,
    HeapAttributes,
} from '../../../../utils/heap-data';
import styles from './SearchBar.module.scss';

const LazySearchTakeover = loadable.lib(
    () => import('@eventbrite/search-takeover'),
);

const queryClient = new QueryClient(DEFAULT_QUERYCLIENT_OPTIONS);
const currentLocationString = gettext('Current location');
const currentMapAreaString = gettext('Map area');

const SearchBarContent = () => {
    const { searchBar } = useGlobalNavContext();
    const { location, isUsingUserLocation } = useLocationContext();
    const { Teams, Features, Components, Actions } = HeapAttributes;

    const [showSearch, setShowSearch] = useState(false);

    const handleClick = () => {
        setShowSearch(true);
    };

    if (!searchBar?.renderSearchBar) {
        return null;
    }

    let locationString;
    if (isUsingUserLocation) {
        // If the user is using their current location, show the "Current location" string
        locationString = currentLocationString;
    } else if (location?.currentPlace) {
        // If we have a current place, show that
        locationString = location?.currentPlace;
    } else if (
        !location?.placeId &&
        location?.latitude &&
        location?.longitude
    ) {
        // If we don't know the placeId, but we have a lat/long (bbox), show "Map area"
        locationString = currentMapAreaString;
    } else if (typeof location === 'undefined') {
        // If location is undefined, show loading state (empty string)
        locationString = '';
    } else {
        // Error fallback: location is probably malformed, show "Current location"
        locationString = currentLocationString;
    }

    return (
        <div
            className={styles.searchBarWithLocation}
            data-testid="header-search"
            onClick={handleClick}
            onKeyDown={(e) => onEnterKey(e, handleClick)}
            aria-label={gettext('open search bar').toString()}
            data-heap-id={getHeapDataAttribute({
                team: Teams.SEO,
                feature: Features.GlobalNav,
                component: Components.SearchBar,
                name: 'search-bar',
                action: Actions.Click,
            })}
        >
            <Typography
                variant="body-md"
                as="span"
                color="neutral-700"
                className={styles.searchBarContent}
            >
                <div className={styles.searchLabelContainer}>
                    <MagnifyingGlassIcon size="small" />
                    <div className={styles.labelsWrapper}>
                        <span className={styles.desktopLabel}>
                            {searchBar?.desktopLabel}
                        </span>
                        <span className={styles.mobileLabel}>
                            {searchBar?.mobileLabel}
                        </span>
                        <span className={styles.mobileLocation}>
                            {locationString}
                        </span>
                    </div>
                </div>
                <div className={styles.dividerAndLocationContainer}>
                    <div className={styles.dividerContainer}>
                        <Divider direction="vertical" color="ui-500" />
                    </div>
                    <div className={styles.locationContainer}>
                        <MapPinIcon size="small" />
                        {locationString}
                    </div>
                </div>
                <div className={styles.locationIconContainer}>
                    <MapPinIcon size="small" />
                </div>
            </Typography>
            {showSearch ? (
                <LazySearchTakeover data-testid="search-takeover">
                    {({ SearchTakeover }: { SearchTakeover: any }) => (
                        <SearchTakeover
                            domNodeId="root"
                            onClose={() => setShowSearch(false)}
                            id="search-takeover"
                            isVisible={showSearch}
                            location={location}
                        />
                    )}
                </LazySearchTakeover>
            ) : null}
        </div>
    );
};

const SearchBar = () => {
    const { searchBar } = useGlobalNavContext();
    return (
        <QueryClientProvider client={queryClient}>
            <LocationProvider location={searchBar?.location}>
                <SearchBarContent />
            </LocationProvider>
        </QueryClientProvider>
    );
};

export default SearchBar;
